import { useEffect, useRef } from 'react';

/**
 * Custom hook to poll a callback function at a specified interval
 * for a specified duration, with an option to stop polling.
 *
 * If we move to RTK Query, we can use pollingInterval instead of this hook.
 */
interface UsePollOptions {
  callback: () => void;
  duration: number;
  initialCall?: boolean;
  interval: number;
  onTimeout: () => void;
  shouldStop: boolean;
}

export function usePoll({
  callback,
  duration,
  initialCall = true,
  interval,
  onTimeout,
  shouldStop,
}: UsePollOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (shouldStop) return;

    if (initialCall) {
      callback();
    }

    intervalRef.current = setInterval(callback, interval);
    timeoutRef.current = setTimeout(() => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      onTimeout();
    }, duration);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [callback, interval, duration, shouldStop, initialCall, onTimeout]);
}
